import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

const FavoritesContext = createContext();

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState(() => {
    try {
      const saved = localStorage.getItem('disqover_favorites');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem('disqover_favorites', JSON.stringify(favorites));
  }, [favorites]);

  const addFavorite = useCallback((businessId) => {
    setFavorites(prev => {
      if (!prev.includes(businessId)) {
        return [...prev, businessId];
      }
      return prev;
    });
  }, []);

  const removeFavorite = useCallback((businessId) => {
    setFavorites(prev => prev.filter(id => id !== businessId));
  }, []);

  const isFavorite = useCallback((businessId) => {
    return favorites.includes(businessId);
  }, [favorites]);

  const toggleFavorite = useCallback((businessId) => {
    setFavorites(prev => {
      if (prev.includes(businessId)) {
        return prev.filter(id => id !== businessId);
      }
      return [...prev, businessId];
    });
  }, []);

  const value = useMemo(() => ({
    favorites, addFavorite, removeFavorite, isFavorite, toggleFavorite
  }), [favorites, addFavorite, removeFavorite, isFavorite, toggleFavorite]);

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};
